import { useState } from 'react';
import { UserCog, Shield } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { EmulateUserModal } from '@/components/admin/EmulateUserModal';
import { useImpersonationState } from '@/hooks/useImpersonation';

interface UserAdminActionsCardProps {
  targetUserId: string;
  targetEmail: string | null;
  targetFirstName: string | null;
  targetLastName: string | null;
}

function joinName(first: string | null, last: string | null, fallback: string | null): string {
  const parts = [first, last].filter((p): p is string => !!p && p.trim().length > 0);
  if (parts.length > 0) return parts.join(' ');
  return fallback ?? '(unknown)';
}

/**
 * Admin actions panel: high-stakes operations on the target user.
 * Today: Emulate user. Future: suspend / delete / force sign-out.
 *
 * The Emulate button is disabled when an emulation is already running
 * — the table enforces this server-side too via the filtered unique
 * index, but disabling the button is the kinder UX.
 */
export function UserAdminActionsCard({
  targetUserId,
  targetEmail,
  targetFirstName,
  targetLastName,
}: UserAdminActionsCardProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const impersonation = useImpersonationState();
  const alreadyImpersonating = impersonation.active;
  const displayName = joinName(targetFirstName, targetLastName, targetEmail);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Shield className="h-4 w-4 text-muted-foreground" />
          Admin actions
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="destructive"
            onClick={() => setOpen(true)}
            disabled={alreadyImpersonating || !targetEmail}
            title={!targetEmail ? 'Target user has no email on file' : undefined}
          >
            <UserCog className="h-4 w-4 mr-2" />
            Emulate user
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Emulation requires a reason, your password, and is audit-logged.
          User is notified 24h after the session ends (unless suppressed).
        </p>
        {alreadyImpersonating && (
          <p className="mt-1 text-xs text-amber-700">
            You already have an active emulation. Stop the current one before starting another.
          </p>
        )}
        {targetEmail && (
          <EmulateUserModal
            open={open}
            onOpenChange={setOpen}
            targetUserId={targetUserId}
            targetEmail={targetEmail}
            targetName={displayName}
          />
        )}
      </CardContent>
    </Card>
  );
}
